/// <amd-module name="Wit/ClientWorkItemService/IClientWorkItem" />

import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { FilterClause } from "Wit/Clients/Work/Contracts/Work";
import * as Resources from "Wit/ClientWorkItemService/Resources";
import { CoreFieldRefNames, FieldType, OobFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getFieldOperator } from "Wit/Common/Predicate";
import { WorkItemPredicateEvaluationHelper } from "Wit/Common/PredicateWIT";
import { getBoardsMessagesService, getErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { WorkItem } from "Wit/WorkItemModel/WorkItem/WorkItem";

export const enum FieldRefNames {
    StartDate = "Microsoft.VSTS.Scheduling.StartDate",
    TargetDate = "Microsoft.VSTS.Scheduling.TargetDate",
    StackRank = "Microsoft.VSTS.Common.StackRank"
}

export const enum ParentField {
    Title = "ParentTitle",
    WorkItemType = "ParentWorkItemType"
}

/**
 * Fields that only exist on the client side and should never be persisted to the server
 */
export const ClientWorkItemFields = new Set<string>([ParentField.Title, ParentField.WorkItemType]);

export interface IClientWorkItem {
    id: number;
    /**
     * Used as a fallback for sorting in the UI when items don't have StackRank. Not persisted to server.
     */
    clientSideRank: number;
    backgroundColor: string | undefined;
    titleStyle: ITitleStyle | undefined;
    saving: boolean;
    /**
     * The work item model for this item. This is set when the item is hydrated.
     * Set manually only if you want to bypass the hydration process.
     */
    readonly iWorkItem: IObservableValue<IWorkItem | undefined>;
    clearWorkItemModel(): void;
    getFields(): INamedFieldDataDictionary;
    getFieldValue<T>(fieldName: string): T | undefined | null;
    getFieldValue<T>(fieldName: string, throwOnMissing: true): T;
    getFieldValue<T>(fieldName: string, throwOnMissing: true, convertToExternal?: boolean): T;
    getFieldValue<T>(fieldName: string, throwOnMissing?: true, convertToExternal?: boolean, asIdentityRef?: boolean): T | undefined | null;
    getRequiredFieldValue<T>(fieldName: string): T;
    setFieldValue<T>(fieldName: string, value: T): void;
    updateBackgroundColor(pageContext: IVssPageContext, styleRules?: ICardStyle[], teamId?: string): Promise<boolean>;
    isCardRuleMatch(pageContext: IVssPageContext, rule: ICardRule, teamId?: string): Promise<boolean>;
    /**
     * Load the IWorkItem representing this item.
     */
    hydrate(pageContext: IVssPageContext, workItem?: Promise<IWorkItem>, includeHistory?: boolean): Promise<IWorkItem>;
    refresh(pageContext: IVssPageContext, config?: { doNotResetIfManuallyDirty?: boolean; includeHistory?: boolean }): Promise<boolean>;
    isValid(pageContext: IVssPageContext): Promise<boolean>;
    save(pageContext: IVssPageContext): Promise<void>;
}

export interface ICardRule {
    name: string;
    clauses: FilterClause[];
}

export interface ICardStyle extends ICardRule {
    backgroundColor: string;
    isEnabled: boolean;
    titleStyle: ITitleStyle | undefined;
}

export interface ITitleStyle {
    isTitleBold?: boolean;
    isTitleItalicized?: boolean;
    isTitleUnderlined?: boolean;
    titleColor?: string;
}

export interface IObservableClientWorkItem<T extends IClientWorkItem = IClientWorkItem> extends IObservableValue<T> {
    rev: number;
    notify(): void;
    refresh(pageContext: IVssPageContext, includeHistory?: boolean): Promise<boolean>;
    saving(): void;
    saveComplete(): void;
    /**
     * Check to see if the item's parent link has changed and update its parent info if so.
     */
    updateParentTitle(
        pageContext: IVssPageContext,
        originalParentId: number | undefined,
        getObservableWorkItem: (id: number) => IObservableClientWorkItem | undefined
    ): void;
    updateBackgroundColor(pageContext: IVssPageContext, styleRules?: ICardStyle[], teamId?: string): Promise<void>;
}

export enum WorkItemEvents {
    /** used when a work item's style has changed */
    ComputedStyleUpdated = "ComputedStyleUpdated",
    /** used when the work item is changed */
    Set = "Set",
    /** used when a work item begins saving. listeners should also listen to the Set event and check the saving property */
    Saving = "Saving",
    SaveComplete = "SaveComplete"
}

/**
 * Given a type that extends IObservableClientWorkItem, return the work item interface it observes.
 */
export type ObservedWorkItem<TObservableWorkItem> = TObservableWorkItem extends IObservableClientWorkItem<infer TWorkItem> ? TWorkItem : never;

export class ClientWorkItem implements IClientWorkItem {
    public get id(): number {
        const id = this.getFieldValue<number>(CoreFieldRefNames.Id, /* throwOnMissing */ true);
        if (!id && this.iWorkItem.value?.tempId) {
            return this.iWorkItem.value.tempId;
        }
        return id;
    }

    public set id(id: number) {
        this.setFieldValue(CoreFieldRefNames.Id, id, /* queueChange */ false);
    }

    public get saving(): boolean {
        return this.savingCounter > 0;
    }

    public set saving(saving: boolean) {
        this.savingCounter = saving ? this.savingCounter + 1 : Math.max(0, this.savingCounter - 1);
    }
    private _queuedChanges: INamedFieldDataDictionary | undefined;
    private _referenceFields: IFieldEntry[];
    private _hydratePromise: Promise<IWorkItem> | undefined;

    private locked = false;
    private savingCounter = 0;
    protected _data: INamedFieldDataDictionary;
    protected _extensionId: string | undefined;
    public iWorkItem: IObservableValue<IWorkItem | undefined>;
    /** used as a fallback for sorting items that don't have a defined StackRank */
    public clientSideRank: number;
    public backgroundColor: string | undefined;
    public titleStyle: ITitleStyle | undefined;

    public constructor(fields: INamedFieldDataDictionary, referenceFields?: IFieldEntry[]) {
        this._referenceFields = referenceFields || [];
        this._data = fields;
        this.iWorkItem = new ObservableValue(undefined);
        if (!this.getFieldValue(CoreFieldRefNames.Id)) {
            this.id = WorkItem.getTempId();
        }

        // items that don't have stack rank are ordered by their id
        // but we want to leave space in here so that we can change the rank for reordering
        this.clientSideRank = this.id * 1000;
    }

    public clearWorkItemModel() {
        this.iWorkItem.value = undefined;
        this._hydratePromise = undefined;
    }

    public getFields() {
        return this._data;
    }

    public getFieldValue<T>(fieldName: string): T | undefined | null;
    public getFieldValue<T>(fieldName: string, throwOnMissing: true): T;
    public getFieldValue<T>(fieldName: string, throwOnMissing?: boolean, convertToExternal?: boolean): T;
    public getFieldValue<T>(fieldName: string, throwOnMissing?: boolean, convertToExternal?: boolean): T | undefined | null
    public getFieldValue<T>(fieldName: string, throwOnMissing?: boolean, convertToExternal?: boolean, asIdentityRef?: boolean): T | undefined | null {
        if (this.iWorkItem.value && !ClientWorkItemFields.has(fieldName)) {
            return this.iWorkItem.value.getFieldValueByName(
                fieldName,
                undefined /*original*/,
                undefined /*omitSetByRule*/,
                asIdentityRef,
                convertToExternal
            );
        }

        if (throwOnMissing) {
            if (!(fieldName in this._data)) {
                throw Error(`Could not find field ${fieldName} on item ${this.id}`);
            }
            if (this._data[fieldName] === null) {
                throw Error(`Field ${fieldName} is null on item ${this.id}`);
            }
            if (this._data[fieldName] === undefined) {
                throw Error(`Field ${fieldName} is undefined on item ${this.id}`);
            }
        }

        return this._data[fieldName] as T | undefined | null;
    }

    public getRequiredFieldValue<T>(fieldName: string): T {
        return this.getFieldValue<T>(fieldName, /* throwOnMissing */ true);
    }

    public setFieldValue<T>(fieldName: string, value: T, queueChange = true) {
        if (this.locked) {
            console.warn(`Cannot update work item ${this.id} while it is saving.`);
            return;
        }

        // Parent field can't be changed (it gets updated via work item links)
        // but client code needs to set it sometimes
        const clientSideChange = ClientWorkItemFields.has(fieldName) || fieldName === CoreFieldRefNames.Parent;
        if (this.iWorkItem.value && !clientSideChange) {
            this.iWorkItem.value.setFieldValueByName(fieldName, value);
        }

        if (queueChange && !clientSideChange) {
            if (!this._queuedChanges) {
                this._queuedChanges = {};
            }
            this._queuedChanges[fieldName] = value;
        }

        this._data[fieldName] = value;
    }

    public async updateBackgroundColor(pageContext: IVssPageContext, styleRules?: ICardStyle[], teamId?: string): Promise<boolean> {
        const prevColor = this.backgroundColor;
        const prevTitleStyles = this.titleStyle;
        let color: string | undefined = undefined;
        let titleRules: ITitleStyle | undefined = undefined;
        if (styleRules) {
            const styleRule = await this.firstMatchingRule(pageContext, styleRules, teamId);
            if (styleRule) {
                color = styleRule.backgroundColor;
                titleRules = {
                    isTitleBold: styleRule.titleStyle?.isTitleBold,
                    isTitleItalicized: styleRule.titleStyle?.isTitleItalicized,
                    isTitleUnderlined: styleRule.titleStyle?.isTitleUnderlined,
                    titleColor: styleRule.titleStyle?.titleColor
                };
            }
        }

        if (didStyleChange(prevColor, color, prevTitleStyles, titleRules)) {
            this.backgroundColor = color;
            this.titleStyle = titleRules;
            return true;
        } else {
            return false;
        }
    }

    private async firstMatchingRule(pageContext: IVssPageContext, styleRules: ICardStyle[], teamId?: string): Promise<ICardStyle | undefined> {
        for (const styleRule of styleRules) {
            if (styleRule.isEnabled && (await this.isCardRuleMatch(pageContext, styleRule, teamId))) {
                return styleRule;
            }
        }
        return undefined;
    }

    public async isCardRuleMatch(pageContext: IVssPageContext, styleRule: ICardRule, teamId?: string): Promise<boolean> {
        const pageData = pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        const identity = pageData.user;
        for (const clause of styleRule.clauses) {
            try {
                const fieldType = this._referenceFields.find(field => field.referenceName === clause.fieldName)?.type;
                const fieldValue = this.getFieldValue(
                    clause.fieldName,
                    undefined /*throwOnMissing*/,
                    fieldType === FieldType.Boolean /*convertToExternal*/
                );

                if (
                    (fieldValue !== null && fieldValue !== undefined) ||
                    fieldType === FieldType.Integer ||
                    fieldType === FieldType.Double ||
                    fieldType === FieldType.DateTime ||
                    fieldType === FieldType.Boolean ||
                    fieldType === FieldType.PlainText ||
                    fieldType === FieldType.String
                ) {
                    const fieldPredicate = getFieldOperator(clause.operator, clause.fieldName, clause.value);
                    const match = await fieldPredicate.evaluate(
                        new WorkItemPredicateEvaluationHelper(pageContext, fieldValue, fieldType, identity, teamId)
                    );
                    if (!match) {
                        return false;
                    }
                } else {
                    return false;
                }
            } catch (err) {
                const ruleError = format(Resources.StyleSavingError, styleRule.name);
                getBoardsMessagesService(pageContext).showMessage(getErrorMessage(ruleError, [err.toString()]));
                return false;
            }
        }
        return true;
    }

    public async hydrate(pageContext: IVssPageContext, workItem?: Promise<IWorkItem>, includeHistory?: boolean): Promise<IWorkItem> {
        const hydrateImpl = async (pageContext: IVssPageContext, workItem?: Promise<IWorkItem>) => {
            const workItemModelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            const workItemPromise = workItem ?? workItemModelService.getWorkItem(this.id, { includeHistory });
            const iWorkItem = await workItemPromise;
            if (this._extensionId && !iWorkItem.extensions.find(e => e.id === this._extensionId)) {
                const extensions = await workItemModelService.getWorkItemTypeExtensions([this._extensionId]);
                iWorkItem.setExtensions(extensions);
            }
            if (this._queuedChanges) {
                for (const [fieldName, value] of Object.entries(this._queuedChanges)) {
                    iWorkItem.setFieldValueByName(fieldName, value);
                }
                this._queuedChanges = undefined;
            }
            this.iWorkItem.value = iWorkItem;
            return iWorkItem;
        };

        if (!this.iWorkItem.value) {
            if (!this._hydratePromise) {
                this._hydratePromise = hydrateImpl(pageContext, workItem);
            }
            return this._hydratePromise;
        } else {
            return this.iWorkItem.value;
        }
    }

    public async refresh(pageContext: IVssPageContext, config?: { doNotResetIfManuallyDirty?: boolean; includeHistory?: boolean }): Promise<boolean> {
        if (this.iWorkItem.value) {
            await this.iWorkItem.value.refresh({
                isDeleted: false,
                doNotResetComment: false,
                doNotResetIfManuallyDirty: config?.doNotResetIfManuallyDirty,
                includeHistory: config?.includeHistory
            });
        } else {
            const modelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            const isCached = modelService.isWorkItemCached(this.id);
            const workItem = await this.hydrate(pageContext);
            if (isCached) {
                await workItem.refresh();
            }
        }

        // Synchronize _data with the fresh work item field data
        // TODO: _data should not be referenced when the iWorkItem is present (so this should not be necessary), but getFields still returns it.
        let updated = false;
        for (const [fieldName, fieldValue] of Object.entries(this._data)) {
            if (ClientWorkItemFields.has(fieldName)) {
                continue;
            }

            const workItemFieldValue = this.iWorkItem.value!.getFieldValueByName(fieldName);
            if ((workItemFieldValue || fieldValue) && workItemFieldValue !== fieldValue) {
                this._data[fieldName] = workItemFieldValue;
                updated = true;
            }
        }

        return updated;
    }

    public async isValid(pageContext: IVssPageContext): Promise<boolean> {
        await this.hydrate(pageContext);
        return this.iWorkItem.value!.isValid();
    }

    public async save(pageContext: IVssPageContext): Promise<void> {
        if (this.locked) {
            return;
        }

        this.locked = true;
        try {
            await this.hydrate(pageContext);
            await this.iWorkItem.value!.save();
        } finally {
            this.locked = false;
        }
    }
}

export class ObservableClientWorkItem<T extends IClientWorkItem = IClientWorkItem>
    extends ObservableValue<T>
    implements IObservableClientWorkItem<T>
{
    public rev = 1;

    public notify() {
        this.rev++;
        super.notify(this.value, WorkItemEvents.Set);
    }

    public async refresh(pageContext: IVssPageContext, includeHistory?: boolean): Promise<boolean> {
        this.saving();
        const updated = await this.value.refresh(pageContext, { includeHistory });
        this.saveComplete();
        return updated;
    }

    public saving() {
        this.rev++;
        this.value.saving = true;
        super.notify(this.value, WorkItemEvents.Saving);
    }

    public saveComplete() {
        this.rev++;
        this.value.saving = false;
        super.notify(this.value, WorkItemEvents.SaveComplete);
    }

    public updateParentTitle(
        pageContext: IVssPageContext,
        originalParentId: number | number,
        getParentObservableWorkItem: (id: number) => IObservableClientWorkItem | undefined
    ) {
        const newParentId = this.value.getFieldValue<number>(CoreFieldRefNames.Parent);
        if (originalParentId !== newParentId) {
            const parent = newParentId ? getParentObservableWorkItem(newParentId) : undefined;
            if (parent) {
                const parentTitle = parent?.value.getFieldValue(CoreFieldRefNames.Title);
                const parentWorkItemType = parent?.value.getFieldValue(CoreFieldRefNames.WorkItemType);
                this.value.setFieldValue(ParentField.Title, parentTitle);
                this.value.setFieldValue(ParentField.WorkItemType, parentWorkItemType);
            } else if (newParentId) {
                // parent exists but isn't available through the IClientWorkItemService, just get the model
                const workItemModelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
                workItemModelService.getWorkItem(newParentId).then(parent => {
                    this.value.setFieldValue(ParentField.Title, parent.getFieldValueByName(CoreFieldRefNames.Title));
                    this.value.setFieldValue(ParentField.WorkItemType, parent.getFieldValueByName(CoreFieldRefNames.WorkItemType));
                    this.notify();
                });
            } else {
                // no parent
                this.value.setFieldValue(ParentField.Title, null);
                this.value.setFieldValue(ParentField.WorkItemType, null);
            }
            this.value.getFields()[CoreFieldRefNames.Parent] = newParentId;
        }
    }

    public async updateBackgroundColor(pageContext: IVssPageContext, styleRules?: ICardStyle[], teamId?: string): Promise<void> {
        const styleUpdated = await this.value.updateBackgroundColor(pageContext, styleRules, teamId);
        if (styleUpdated) {
            super.notify(this.value, WorkItemEvents.ComputedStyleUpdated);
        }
    }
}

export type ObservableItemSorter = (a: IObservableClientWorkItem, b: IObservableClientWorkItem) => number;

export function observableWorkItemStackRankSorter(a: IObservableClientWorkItem, b: IObservableClientWorkItem) {
    return workItemStackRankSorter(a.value, b.value);
}

/**
 * Useful for unit testing
 */
export function workItemStackRankSorter(a: IClientWorkItem, b: IClientWorkItem) {
    return workItemSorter(a, b, FieldRefNames.StackRank);
}

export function getObservableWorkItemSorter(orderField: string = FieldRefNames.StackRank): ObservableItemSorter {
    return (a: IObservableClientWorkItem, b: IObservableClientWorkItem) => workItemSorter(a.value, b.value, orderField);
}

export function workItemSorter(a: IClientWorkItem, b: IClientWorkItem, fieldName: string) {
    let aRank = a.getFieldValue<number>(fieldName);
    let bRank = b.getFieldValue<number>(fieldName);
    if (aRank === undefined || aRank === null) {
        aRank = Number.MAX_SAFE_INTEGER;
    }
    if (bRank === undefined || bRank === null) {
        bRank = Number.MAX_SAFE_INTEGER;
    }
    let result = aRank - bRank;
    if (result === 0) {
        result = a.clientSideRank - b.clientSideRank;
    }
    return result;
}

export function observableWorkItemClosedDateSorter(a: IObservableClientWorkItem, b: IObservableClientWorkItem) {
    return workItemClosedDateSorter(a.value, b.value);
}

/**
 * Sort most recent closed date to furthest closed date.
 * Items without a closed date are sorted to the top.
 */
export function workItemClosedDateSorter(a: IClientWorkItem, b: IClientWorkItem) {
    const aVal = a.getFieldValue<Date>(OobFieldRefNames.ClosedDate);
    const bVal = b.getFieldValue<Date>(OobFieldRefNames.ClosedDate);
    const aRank = aVal instanceof Date ? aVal.getTime() : Number.MAX_SAFE_INTEGER;
    const bRank = bVal instanceof Date ? bVal.getTime() : Number.MAX_SAFE_INTEGER;
    return bRank - aRank;
}

function didStyleChange(
    prevColor: string | undefined,
    newColor: string | undefined,
    prevTitleStyle: ITitleStyle | undefined,
    newTitleStyle: ITitleStyle | undefined
) {
    if (prevColor !== newColor) {
        return true;
    }

    if (prevTitleStyle || newTitleStyle) {
        return (
            prevTitleStyle?.isTitleBold !== newTitleStyle?.isTitleBold ||
            prevTitleStyle?.isTitleItalicized !== newTitleStyle?.isTitleItalicized ||
            prevTitleStyle?.isTitleUnderlined !== newTitleStyle?.isTitleUnderlined ||
            prevTitleStyle?.titleColor !== newTitleStyle?.titleColor
        );
    } else {
        return false;
    }
}
